import { formSchema } from '../Form/schemas';
import { taskTemplateSchema } from '../Task/schemas';
import { createListApiSchema } from '../General/schemas';
import z from 'zod';

export const typeRequirementEntrySchema = z.object({
	key: z.string(),
	name: z.string().optional(),
	optional: z.boolean().default(false),
});

export const zoneProfileMappingSchema = z.object({
	zoneId: z.string(),
	accessProfileId: z.string(),
});

export const visibleFieldsSchema = z.object({
	zones: z.boolean(),
	groups: z.boolean(),
	owners: z.boolean(),
	attendeeEmails: z.boolean().optional(),
});

export const typeSchema = z
	.object({
		id: z.string(),
		archived: z.boolean().default(false),
		archivalPeriod: z.string().optional(),
		name: z.string(),
		model: z.enum(['group', 'event']),
		/** For event types only: 'job' (project jobs) or 'visit' (standalone visits). Mutually exclusive. */
		eventKind: z.enum(['job', 'visit']).optional(),
		/** Managed-group visits (9ID-1867): the organiser manages the group as a whole — visitors are
		 * not invited individually and the advance reminder runs 5 working days ahead instead of 24h. */
		isManagedGroup: z.boolean().default(false),
		tagIds: z.string().array().optional(),
		/** Per-event-type selection of tenant custom event properties to surface on this type's events.
		 *  The global definition (Settings.EventData) owns each field's type; this mapping owns which
		 *  fields are relevant, whether each is required, and their order. */
		eventProps: z.array(z.object({ name: z.string(), required: z.boolean().optional() })).optional(),
		tasks: z.string().array(),
		mandatoryGroupOwner: z.boolean().optional(),
		expiryWarning: z.boolean().default(false),
		adminRoleId: z.string().optional(),
		form: z.string().optional(),
		keywords: z.string().optional(),
		preset: z.any(),
		isInternal: z.boolean().optional(),
		allowChildGroups: z.boolean().optional(),
		inheritUsersFromGroups: z.boolean().optional(),
		lockDelegatedRoles: z.boolean().optional(),
		defaultDurationHours: z.number().optional(),
		defaultStartHour: z.number().optional(),
		defaultStartNow: z.boolean().optional(),
		maxDurationDays: z.number().optional(),
		maxDurationHours: z.number().optional(),
		enableTimeSelection: z.boolean().optional(),
		allowMinutesPre: z.number().optional(),
		allowMinutesPost: z.number().optional(),
		visibleFields: visibleFieldsSchema,
		// Deprecated: use zoneProfileMapping in the admin app instead.
		// Kept for backward compatibility with the legacy manager app.
		possibleRoleIds: z.string().array().optional(),
		assignableByRoleIds: z.string().array().optional(),
		// Deprecated: replaced by zoneProfileMapping in the admin app.
		// Kept for backward compatibility with the legacy manager app.
		intersectPossibleRoleIdsWithZone: z.boolean().default(false),
		zoneProfileMapping: z.array(zoneProfileMappingSchema).optional(),
		requirements: typeRequirementEntrySchema.array(),
		requirementsSubcontractors: typeRequirementEntrySchema.array().optional(),
		Form: z.lazy(() => formSchema).nullable(),
		taskDetails: z.lazy(() => z.array(taskTemplateSchema)).optional(),
	})
	.describe('Type');

export const typesApiResponseSchema = createListApiSchema(typeSchema).describe('TypeList');

export const allTypesApiResponseSchema = z.array(typeSchema).describe('AllTypes');
